/**
 * BasicTab Component
 *
 * Core configuration tab containing:
 * - Basic information (name, description, output format)
 * - Model configuration (new semantic grouping layout)
 * - Team configuration (when team mode is enabled)
 */

import React, { useMemo, useCallback } from 'react';
import { Users } from 'lucide-react';
import { FieldSection } from '@/components/fields/FieldSection';
import { BasicInfoSection } from './BasicInfoSection';
import { ModelsSection } from './ModelsSection';
import { MERGED_FIELD_CONFIG as FIELD_CONFIG, ValidationResult } from '@/config/fieldConfig';
import { FIELD_SECTIONS } from '@/config/fieldSections';
import type { AgentConfiguration } from '@/types';
import type { AgentConfigNavigationContext } from '@/lib/navigation';

// Sections rendered via FieldSection (basic is rendered via BasicInfoSection)
// NOTE: 'models' is now rendered via ModelsSection with custom layout
const FIELD_SECTION_KEYS = [
  'team', // Team section - fields have showIf conditions
];

interface BasicTabProps {
  config: Partial<AgentConfiguration>;
  onChange: (name: string, value: any) => void;
  appContext: any;
  errors?: Record<string, ValidationResult>;
  /** Section to highlight (for scroll-to navigation from Hooks tab) */
  highlightSection?: string | null;
  /** Navigation context for cross-tab/page navigation */
  navigation?: AgentConfigNavigationContext;
}

export const BasicTab: React.FC<BasicTabProps> = ({
  config,
  onChange,
  appContext,
  errors = {},
  highlightSection = null,
  navigation,
}) => {
  const isTeamMode = config.agent_mode === 'team';
  // Group fields by section (excluding basic - rendered separately)
  const groupedFields = useMemo(() => {
    const groups: Record<string, Array<{ name: string; schema: typeof FIELD_CONFIG[string] }>> = {};

    Object.entries(FIELD_CONFIG).forEach(([name, schema]) => {
      const section = schema.section;
      if (FIELD_SECTION_KEYS.includes(section)) {
        if (!groups[section]) {
          groups[section] = [];
        }
        groups[section].push({ name, schema });
      }
    });

    return groups;
  }, []);

  // Check if a section has any visible fields
  const sectionHasVisibleFields = useCallback((sectionKey: string) => {
    const fields = groupedFields[sectionKey];
    if (!fields || fields.length === 0) return false;

    return fields.some((field) => {
      if (!field.schema.showIf) return true;
      return field.schema.showIf(appContext);
    });
  }, [groupedFields, appContext]);

  // Get sections to render via FieldSection
  const fieldSections = useMemo(() => {
    return FIELD_SECTION_KEYS
      .filter(key => FIELD_SECTIONS[key] && groupedFields[key]?.length > 0)
      .map(key => [key, FIELD_SECTIONS[key]] as [string, typeof FIELD_SECTIONS[string]]);
  }, [groupedFields]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-900">
              {isTeamMode ? 'Agent团队' : 'Agent配置'}
            </h2>
            {isTeamMode && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full border border-purple-200">
                <Users className="w-3.5 h-3.5" />
                团队模式
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {isTeamMode
              ? 'Agent基本信息、模型选择、生成参数和团队协作配置'
              : 'Agent基本信息、模型选择和生成参数'
            }
          </p>
        </div>
      </div>

      {/* Basic Information Section (custom layout) */}
      <BasicInfoSection
        values={appContext}
        onChange={onChange}
        errors={errors}
        highlight={highlightSection === 'basic'}
        navigation={navigation}
      />

      {/* Models Section (compact inline layout) */}
      <ModelsSection
        onChange={onChange}
        appContext={appContext}
        highlightSection={highlightSection}
      />

      {/* Other Configuration Sections (Team, etc.) */}
      {fieldSections.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {fieldSections.map(([sectionKey, sectionConfig]) => {
            if (!sectionHasVisibleFields(sectionKey)) {
              return null;
            }
            return (
              <div
                key={sectionKey}
                className={sectionConfig.layout === 1 ? 'xl:col-span-2' : ''}
              >
                <FieldSection
                  sectionKey={sectionKey}
                  sectionConfig={sectionConfig}
                  fields={groupedFields[sectionKey] || []}
                  values={appContext}
                  onChange={onChange}
                  appContext={appContext}
                  errors={errors}
                  highlight={highlightSection === sectionKey}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BasicTab;
